import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const DEFAULT_THEME = {
  logo_url:
    'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg',
  brand_primary: '#C45C26',
  brand_secondary: '#2E7D32',
  nav_bg: '#FFFFFF',
  nav_text: '#5D4E37',
};

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  reload: () => {},
});

/**
 * Injects CSS overrides so that hardcoded Tailwind arbitrary values
 * (e.g. `bg-[#C45C26]`, `text-[#2E7D32]`) follow the runtime theme.
 * This avoids refactoring every component.
 */
function buildOverrides(t) {
  const p = t.brand_primary || DEFAULT_THEME.brand_primary;
  const s = t.brand_secondary || DEFAULT_THEME.brand_secondary;
  // Tailwind escapes [ ] # as \[ \] \# in the generated CSS selector
  const esc = (hex) => hex.replace('#', '\\#');
  const P = esc('#C45C26');
  const P2 = esc('#A84B1F'); // hover variant of terracotta
  const S = esc('#2E7D32');
  const S2 = esc('#1B5E20'); // hover variant of green

  // Derive slightly darker hover colors from primary/secondary
  const darken = (hex, pct = 15) => {
    const num = parseInt(hex.slice(1), 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    r = Math.max(0, Math.floor(r * (1 - pct / 100)));
    g = Math.max(0, Math.floor(g * (1 - pct / 100)));
    b = Math.max(0, Math.floor(b * (1 - pct / 100)));
    return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
  };
  const pHover = darken(p);
  const sHover = darken(s);

  return `
    /* Runtime theme overrides */
    :root {
      --brand-primary: ${p};
      --brand-secondary: ${s};
      --nav-bg: ${t.nav_bg || DEFAULT_THEME.nav_bg};
      --nav-text: ${t.nav_text || DEFAULT_THEME.nav_text};
    }
    .bg-\\[${P}\\] { background-color: ${p} !important; }
    .text-\\[${P}\\] { color: ${p} !important; }
    .border-\\[${P}\\] { border-color: ${p} !important; }
    .hover\\:bg-\\[${P}\\]:hover { background-color: ${p} !important; }
    .hover\\:text-\\[${P}\\]:hover { color: ${p} !important; }
    .from-\\[${P}\\] { --tw-gradient-from: ${p} var(--tw-gradient-from-position) !important; --tw-gradient-to: ${p}00 var(--tw-gradient-to-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
    .to-\\[${P}\\] { --tw-gradient-to: ${p} var(--tw-gradient-to-position) !important; }
    .bg-\\[${P2}\\] { background-color: ${pHover} !important; }
    .hover\\:bg-\\[${P2}\\]:hover { background-color: ${pHover} !important; }
    .text-\\[${P2}\\] { color: ${pHover} !important; }
    .hover\\:text-\\[${P2}\\]:hover { color: ${pHover} !important; }
    .border-\\[${P2}\\] { border-color: ${pHover} !important; }

    .bg-\\[${S}\\] { background-color: ${s} !important; }
    .text-\\[${S}\\] { color: ${s} !important; }
    .border-\\[${S}\\] { border-color: ${s} !important; }
    .hover\\:bg-\\[${S}\\]:hover { background-color: ${s} !important; }
    .hover\\:text-\\[${S}\\]:hover { color: ${s} !important; }
    .from-\\[${S}\\] { --tw-gradient-from: ${s} var(--tw-gradient-from-position) !important; --tw-gradient-to: ${s}00 var(--tw-gradient-to-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
    .to-\\[${S}\\] { --tw-gradient-to: ${s} var(--tw-gradient-to-position) !important; }
    .bg-\\[${S2}\\] { background-color: ${sHover} !important; }
    .hover\\:bg-\\[${S2}\\]:hover { background-color: ${sHover} !important; }
  `;
}

function applyStyleTag(css) {
  let tag = document.getElementById('site-theme-overrides');
  if (!tag) {
    tag = document.createElement('style');
    tag.id = 'site-theme-overrides';
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}

// Patterns of the default hardcoded logo URLs used across the app.
// Any <img> whose src matches these will be replaced with the current theme logo.
const LOGO_PATTERNS = [
  'LOGO%20DEF',
  'LOGO DEF',
  '6cemh04i_LOGO',
];

let currentLogoUrl = null;

function replaceAllLogos(newUrl) {
  currentLogoUrl = newUrl;
  if (!newUrl) return;
  const imgs = document.querySelectorAll('img');
  imgs.forEach((img) => {
    const src = img.getAttribute('src') || '';
    if (LOGO_PATTERNS.some((p) => src.includes(p)) && src !== newUrl) {
      img.setAttribute('data-original-src', src);
      img.setAttribute('src', newUrl);
    }
  });
}

let observerStarted = false;
function startLogoObserver() {
  if (observerStarted) return;
  observerStarted = true;
  const observer = new MutationObserver((mutations) => {
    if (!currentLogoUrl) return;
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const imgs = node.tagName === 'IMG' ? [node] : node.querySelectorAll?.('img') || [];
        imgs.forEach((img) => {
          const src = img.getAttribute('src') || '';
          if (LOGO_PATTERNS.some((p) => src.includes(p)) && src !== currentLogoUrl) {
            img.setAttribute('data-original-src', src);
            img.setAttribute('src', currentLogoUrl);
          }
        });
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(DEFAULT_THEME);

  const load = () => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/site-theme`)
      .then((res) => {
        const next = { ...DEFAULT_THEME, ...res.data };
        setThemeState(next);
        applyStyleTag(buildOverrides(next));
        replaceAllLogos(next.logo_url);
        startLogoObserver();
      })
      .catch(() => {
        applyStyleTag(buildOverrides(DEFAULT_THEME));
        startLogoObserver();
      });
  };

  useEffect(() => {
    load();
  }, []);

  const setTheme = (patch) => {
    setThemeState((prev) => {
      const next = { ...prev, ...patch };
      applyStyleTag(buildOverrides(next));
      if (patch.logo_url) replaceAllLogos(patch.logo_url);
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, reload: load }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
